// src/case-conversion/index.ts
function pascalToCamel(str) {
  if (!str || str.length === 0 || !str[0]) return str;
  return str[0].toLowerCase() + str.slice(1);
}
function snakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}
function camelToSnake(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
function convertKeysToCamel(obj) {
  if (obj === null || obj === void 0) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToCamel(item));
  }
  if (typeof obj === "object" && obj.constructor === Object) {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = snakeToCamel(key);
        result[camelKey] = convertKeysToCamel(obj[key]);
      }
    }
    return result;
  }
  return obj;
}
function convertKeysToSnake(obj) {
  if (obj === null || obj === void 0) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToSnake(item));
  }
  if (typeof obj === "object" && obj.constructor === Object) {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const snakeKey = camelToSnake(key);
        result[snakeKey] = convertKeysToSnake(obj[key]);
      }
    }
    return result;
  }
  return obj;
}
function camelToPascal(str) {
  if (!str || str.length === 0 || !str[0]) return str;
  return str[0].toUpperCase() + str.slice(1);
}
function convertCamelKeysToPascal(obj) {
  if (obj === null || obj === void 0) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => convertCamelKeysToPascal(item));
  }
  if (typeof obj === "object" && obj.constructor === Object) {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const pascalKey = camelToPascal(key);
        result[pascalKey] = convertCamelKeysToPascal(obj[key]);
      }
    }
    return result;
  }
  return obj;
}
function convertPascalKeysToCamel(obj) {
  if (obj === null || obj === void 0) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => convertPascalKeysToCamel(item));
  }
  if (typeof obj === "object" && obj.constructor === Object) {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = pascalToCamel(key);
        result[camelKey] = convertPascalKeysToCamel(obj[key]);
      }
    }
    return result;
  }
  return obj;
}

// src/normalization/domain.ts
function normalizeDomain(hostname) {
  return hostname.replace(/^www\./i, "").replace(/:.*/, "").toLowerCase().trim();
}
function extractHostDomainFromOrigin(origin) {
  if (!origin || origin === "null") return null;
  try {
    const url = new URL(origin);
    if (!url.hostname) return null;
    return normalizeDomain(url.hostname);
  } catch {
    return null;
  }
}

// src/product-sorting.ts
var StockStatus = /* @__PURE__ */ ((StockStatus2) => {
  StockStatus2[StockStatus2["IN_STOCK"] = 0] = "IN_STOCK";
  StockStatus2[StockStatus2["BACKORDERABLE"] = 1] = "BACKORDERABLE";
  StockStatus2[StockStatus2["OUT_OF_STOCK"] = 2] = "OUT_OF_STOCK";
  return StockStatus2;
})(StockStatus || {});
var CategoryPriority = /* @__PURE__ */ ((CategoryPriority2) => {
  CategoryPriority2[CategoryPriority2["REGULAR"] = 0] = "REGULAR";
  CategoryPriority2[CategoryPriority2["WELLNESS"] = 1] = "WELLNESS";
  CategoryPriority2[CategoryPriority2["MERCH_PROMO"] = 2] = "MERCH_PROMO";
  return CategoryPriority2;
})(CategoryPriority || {});
function normalizeQuantity(quantity) {
  if (quantity === null || quantity === void 0) {
    return 0;
  }
  if (typeof quantity === "string") {
    const parsed = parseFloat(quantity);
    return isNaN(parsed) ? 0 : parsed;
  }
  return quantity;
}
function getTotalInventory(product) {
  if (product.quantities && product.quantities.length > 0) {
    return product.quantities.reduce((total, warehouse) => {
      const warehouseQty = normalizeQuantity(warehouse.quantity);
      return total + warehouseQty;
    }, 0);
  }
  return normalizeQuantity(product.quantity);
}
function isInStock(product) {
  const quantity = getTotalInventory(product);
  return quantity > 0;
}
function isBackorderable(product) {
  const quantity = getTotalInventory(product);
  const canBackorder = product.canBackorder ?? product.can_backorder ?? null;
  return quantity <= 0 && canBackorder === true;
}
function getProductStockStatus(product) {
  if (isInStock(product)) {
    return 0 /* IN_STOCK */;
  }
  if (isBackorderable(product)) {
    return 1 /* BACKORDERABLE */;
  }
  return 2 /* OUT_OF_STOCK */;
}
function getCategoryPriority(product) {
  const categoryPath = (product.categoryPath ?? product.category_path ?? "").toLowerCase();
  if (categoryPath.includes("merch & promo")) {
    return 2 /* MERCH_PROMO */;
  }
  if (categoryPath.includes("wellness")) {
    return 1 /* WELLNESS */;
  }
  return 0 /* REGULAR */;
}
var stockStatusComparator = (a, b) => {
  const statusA = getProductStockStatus(a);
  const statusB = getProductStockStatus(b);
  return statusA - statusB;
};
var categoryComparator = (a, b) => {
  const priorityA = getCategoryPriority(a);
  const priorityB = getCategoryPriority(b);
  return priorityA - priorityB;
};
function combineComparators(...comparators) {
  return (a, b) => {
    for (const comparator of comparators) {
      const result = comparator(a, b);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };
}
var defaultProductComparator = combineComparators(
  stockStatusComparator,
  categoryComparator
);
function sortProducts(products, comparator = defaultProductComparator) {
  const productsCopy = [...products];
  const finalComparator = Array.isArray(comparator) ? combineComparators(...comparator) : comparator;
  return productsCopy.sort(finalComparator);
}
function sortProductsWithOptions(products, options) {
  if (options?.customComparator) {
    return sortProducts(products, options.customComparator);
  }
  return sortProducts(products);
}

// src/phoneNumber.utils.ts
function formatPhoneNumber(phoneNumber) {
  const digits = phoneNumber.replace(/\D/g, "");
  let normalizedDigits = digits;
  if (digits.length === 11 && digits[0] === "1") {
    normalizedDigits = digits.slice(1);
  }
  if (normalizedDigits.length !== 10) {
    return phoneNumber;
  }
  return `${normalizedDigits.slice(0, 3)}-${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6, 10)}`;
}
function normalizePhoneNumber(phoneNumber) {
  const trimmed = phoneNumber.trim();
  if (!trimmed) {
    return null;
  }
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) {
    return null;
  }
  let normalizedDigits;
  if (digits.length === 10) {
    normalizedDigits = digits;
  } else if (digits.length === 11 && digits[0] === "1") {
    normalizedDigits = digits.slice(1);
  } else {
    return null;
  }
  if (normalizedDigits.length !== 10) {
    return null;
  }
  return `+1${normalizedDigits}`;
}

// src/feature-flags.utils.ts
function parseCustomerIdList(envValue) {
  if (!envValue) return [];
  return envValue.split(",").map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id) && id > 0);
}
function hasAchCheckoutAccess(customerId, betaCustomers) {
  if (!customerId || customerId <= 0) return false;
  return betaCustomers.includes(customerId);
}

// src/category.utils.ts
function buildCategoryTree(categories) {
  const categoryMap = /* @__PURE__ */ new Map();
  const roots = [];
  for (const cat of categories) {
    categoryMap.set(cat.id, { category: cat, children: [] });
  }
  for (const cat of categories) {
    const node = categoryMap.get(cat.id);
    if (cat.parentId === null || cat.parentId === void 0) {
      roots.push(node);
    } else {
      const parent = categoryMap.get(cat.parentId);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  }
  const sortChildren = (nodes) => {
    nodes.sort((a, b) => a.category.name.localeCompare(b.category.name));
    for (const node of nodes) {
      sortChildren(node.children);
    }
  };
  sortChildren(roots);
  return roots;
}
function getCategoryWithDescendantIds(categoryId, categories) {
  const result = [categoryId];
  const childrenOf = (parentId) => {
    for (const cat of categories) {
      if (cat.parentId === parentId) {
        result.push(cat.id);
        childrenOf(cat.id);
      }
    }
  };
  childrenOf(categoryId);
  return result;
}
function flattenCategoryTree(categories, options = {}) {
  const { indentPrefix = "\u2014 " } = options;
  const tree = buildCategoryTree(categories);
  const result = [];
  const traverse = (nodes, depth) => {
    for (const node of nodes) {
      const { category, children } = node;
      const indent = depth > 0 ? indentPrefix.repeat(depth) : "";
      result.push({
        id: category.id.toString(),
        name: category.name,
        depth,
        hasChildren: children.length > 0,
        parentId: category.parentId?.toString() ?? null,
        displayName: `${indent}${category.name}`
      });
      traverse(children, depth + 1);
    }
  };
  traverse(tree, 0);
  return result;
}
function groupCategoriesByParent(categories) {
  const tree = buildCategoryTree(categories);
  const groups = [];
  for (const node of tree) {
    if (node.children.length > 0) {
      groups.push({
        parent: node.category,
        children: node.children.map((c) => c.category)
      });
    } else {
      groups.push({
        parent: null,
        children: [node.category]
      });
    }
  }
  return groups;
}
function isParentCategory(categoryId, categories) {
  return categories.some((c) => c.parentId === categoryId);
}
function getRootParent(categoryId, categories) {
  const category = categories.find((c) => c.id === categoryId);
  if (!category) return null;
  if (category.parentId === null || category.parentId === void 0) {
    return category;
  }
  return getRootParent(category.parentId, categories);
}

// src/state.utils.ts
var US_STATES = {
  "alabama": "AL",
  "alaska": "AK",
  "arizona": "AZ",
  "arkansas": "AR",
  "california": "CA",
  "colorado": "CO",
  "connecticut": "CT",
  "delaware": "DE",
  "florida": "FL",
  "georgia": "GA",
  "hawaii": "HI",
  "idaho": "ID",
  "illinois": "IL",
  "indiana": "IN",
  "iowa": "IA",
  "kansas": "KS",
  "kentucky": "KY",
  "louisiana": "LA",
  "maine": "ME",
  "maryland": "MD",
  "massachusetts": "MA",
  "michigan": "MI",
  "minnesota": "MN",
  "mississippi": "MS",
  "missouri": "MO",
  "montana": "MT",
  "nebraska": "NE",
  "nevada": "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  "ohio": "OH",
  "oklahoma": "OK",
  "oregon": "OR",
  "pennsylvania": "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  "tennessee": "TN",
  "texas": "TX",
  "utah": "UT",
  "vermont": "VT",
  "virginia": "VA",
  "washington": "WA",
  "west virginia": "WV",
  "wisconsin": "WI",
  "wyoming": "WY",
  "district of columbia": "DC"
};
function toStateAbbreviation(state) {
  if (!state) return "";
  const trimmed = state.trim();
  if (trimmed.length === 2) {
    return trimmed.toUpperCase();
  }
  const normalized = trimmed.toLowerCase();
  const abbreviation = US_STATES[normalized];
  if (abbreviation) {
    return abbreviation;
  }
  return trimmed;
}
function isValidUSState(state) {
  if (!state) return false;
  const trimmed = state.trim();
  if (trimmed.length === 2) {
    const upperCode = trimmed.toUpperCase();
    return Object.values(US_STATES).includes(upperCode);
  }
  return US_STATES[trimmed.toLowerCase()] !== void 0;
}

// src/geo/distance.ts
var EARTH_RADIUS_KM = 6371;
var EARTH_RADIUS_MILES = 3959;
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}
function haversineDistance(a, b) {
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const deltaLat = toRadians(b.lat - a.lat);
  const deltaLng = toRadians(b.lng - a.lng);
  const haversine = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  return EARTH_RADIUS_KM * c;
}
function haversineDistanceMiles(a, b) {
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const deltaLat = toRadians(b.lat - a.lat);
  const deltaLng = toRadians(b.lng - a.lng);
  const haversine = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  return EARTH_RADIUS_MILES * c;
}
function sortByProximity(items, target, getCoords) {
  const withDistances = items.map((item) => {
    const coords = getCoords(item);
    const distanceKm = coords ? haversineDistance(target, coords) : Infinity;
    return { item, distanceKm };
  });
  return withDistances.sort((a, b) => a.distanceKm - b.distanceKm);
}
function findClosest(items, target, getCoords) {
  let closest = null;
  for (const item of items) {
    const coords = getCoords(item);
    if (!coords) continue;
    const distanceKm = haversineDistance(target, coords);
    if (!closest || distanceKm < closest.distanceKm) {
      closest = { item, distanceKm };
    }
  }
  return closest;
}
function isValidCoordinates(coords) {
  if (!coords) return false;
  return typeof coords.lat === "number" && typeof coords.lng === "number" && !isNaN(coords.lat) && !isNaN(coords.lng) && coords.lat >= -90 && coords.lat <= 90 && coords.lng >= -180 && coords.lng <= 180;
}

// src/license.utils.ts
function isValidLicense(result) {
  return "normalized" in result;
}
var MT_LICENSE_PATTERN = /^[MDCT]-\d{5,6}-\d{2,3}$/;
var COMPACT_C_PATTERN = /^C\d{9,}$/;
var PHONE_PATTERN = /^\d{10,}$/;
function parseLicenseNumber(raw) {
  if (raw == null || raw.trim() === "") {
    return { value: null, reason: "empty" };
  }
  let cleaned = raw.trim();
  cleaned = cleaned.replace(/^[^a-zA-Z0-9]+/, "");
  cleaned = cleaned.toUpperCase();
  cleaned = cleaned.replace(/[-\s_.#]+$/, "");
  if (/\s/.test(cleaned)) {
    return { value: raw.trim(), reason: "contains_spaces" };
  }
  if (!/^[MDCT]/.test(cleaned)) {
    return { value: raw.trim(), reason: "invalid_prefix" };
  }
  const digitsOnly = cleaned.replace(/[^0-9]/g, "");
  if (PHONE_PATTERN.test(digitsOnly) && !/[A-Z].*-.*\d/.test(cleaned)) {
    return { value: raw.trim(), reason: "phone_number" };
  }
  if (MT_LICENSE_PATTERN.test(cleaned)) {
    return { value: raw.trim(), normalized: cleaned };
  }
  if (COMPACT_C_PATTERN.test(cleaned)) {
    return { value: raw.trim(), normalized: cleaned };
  }
  return { value: raw.trim(), reason: "invalid_format" };
}
function extractLicenseNumbers(customer) {
  const valid = [];
  const invalid = [];
  const seen = /* @__PURE__ */ new Set();
  const sources = [
    { raw: customer.company_number, source: "company_number" },
    {
      raw: customer.extra_fields["License Number"],
      source: "extra_fields"
    }
  ];
  for (const { raw, source } of sources) {
    const result = parseLicenseNumber(raw);
    if (isValidLicense(result)) {
      if (!seen.has(result.normalized)) {
        seen.add(result.normalized);
        valid.push({ licenseNumber: result.normalized, source });
      }
    } else {
      if (result.reason !== "empty") {
        invalid.push({ raw: result.value, source, reason: result.reason });
      }
    }
  }
  return { valid, invalid };
}
function inferLicenseType(licenseNumber) {
  if (licenseNumber.startsWith("M-") || licenseNumber.startsWith("M")) {
    return "manufacturer";
  }
  if (licenseNumber.startsWith("D-") || licenseNumber.startsWith("D")) {
    return "dispensary";
  }
  if (licenseNumber.startsWith("C")) {
    return "cultivator";
  }
  if (licenseNumber.startsWith("T-") || licenseNumber.startsWith("T")) {
    return "transporter";
  }
  return "combined_use";
}

// src/vehicle.utils.ts
function firstNonEmpty(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}
function prettyType(type) {
  if (!type) return "Vehicle";
  const upper = type.toUpperCase();
  switch (upper) {
    case "CAR":
      return "Car";
    case "TRUCK":
      return "Truck";
    case "MOTORCYCLE":
      return "Motorcycle";
    case "BICYCLE":
      return "Bicycle";
    default:
      return type;
  }
}
function formatVehicleName(vehicle) {
  const makeAndModel = [vehicle.make, vehicle.model].map((value) => value?.trim()).filter((value) => Boolean(value)).join(" ");
  return firstNonEmpty(makeAndModel, vehicle.description) ?? prettyType(vehicle.type);
}
function formatVehicleSubtitle(vehicle) {
  return [vehicle.color, vehicle.licensePlate].map((value) => value?.trim()).filter((value) => Boolean(value)).join(" \xB7 ");
}
function formatVehicleFullDisplay(vehicle) {
  const name = formatVehicleName(vehicle);
  const subtitle = formatVehicleSubtitle(vehicle);
  return subtitle ? `${name} \xB7 ${subtitle}` : name;
}

export {
  pascalToCamel,
  snakeToCamel,
  camelToSnake,
  convertKeysToCamel,
  convertKeysToSnake,
  camelToPascal,
  convertCamelKeysToPascal,
  convertPascalKeysToCamel,
  normalizeDomain,
  extractHostDomainFromOrigin,
  StockStatus,
  CategoryPriority,
  getTotalInventory,
  isInStock,
  isBackorderable,
  getProductStockStatus,
  getCategoryPriority,
  stockStatusComparator,
  categoryComparator,
  combineComparators,
  defaultProductComparator,
  sortProducts,
  sortProductsWithOptions,
  formatPhoneNumber,
  normalizePhoneNumber,
  parseCustomerIdList,
  hasAchCheckoutAccess,
  buildCategoryTree,
  getCategoryWithDescendantIds,
  flattenCategoryTree,
  groupCategoriesByParent,
  isParentCategory,
  getRootParent,
  toStateAbbreviation,
  isValidUSState,
  haversineDistance,
  haversineDistanceMiles,
  sortByProximity,
  findClosest,
  isValidCoordinates,
  isValidLicense,
  parseLicenseNumber,
  extractLicenseNumbers,
  inferLicenseType,
  formatVehicleName,
  formatVehicleSubtitle,
  formatVehicleFullDisplay
};
